// src/components/EventDrawer.jsx
import React, { useState, useEffect } from 'react'
import { Calendar, MapPin, Users, FileText, User, Tag } from 'lucide-react'
import DrawerWrapper from './DrawerWrapper'
import DynamicFieldsEditor, { fieldsForModule } from './shared/DynamicFieldsEditor'
import { useSettings } from '../contexts/SettingsContext'
import AddCustomerModal from './AddCustomerModal'
import PaymentPlanEditor, { emptyPaymentPlan, validatePaymentPlan } from './PaymentPlanEditor'
import { isRealVenueName, sanitizeVenueNames } from '../utils/venues'

const EventDrawer = ({ 
  open, 
  onClose, 
  onSubmit, 
  customersList = [], 
  selectedCustomer, 
  setSelectedCustomer, 
  onCustomerCreated,
  editingEvent,
  statusOptions = ['Pendiente', 'Confirmado', 'Cancelado'],
  eventTypeOptions = ['Boda', 'Cumpleaños', 'Aniversario', 'Corporativo', 'Social']
}) => {
  const { settings } = useSettings()
  const dynamicFields = fieldsForModule(settings?.customFields, 'event')
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [paymentPlan, setPaymentPlan] = useState(() => emptyPaymentPlan())
  const [formData, setFormData] = useState({
    name: '',
    venue: '',
    date: '',
    time: '',
    status: 'Pendiente',
    description: '',
    guests: '',
    eventType: '',
    customFields: {}
  })

  useEffect(() => {
    setCurrentStep(0)
    if (editingEvent) {
      const eventDateTime = editingEvent.date ? new Date(editingEvent.date) : new Date()
      setFormData({
        name: editingEvent.name || '',
        venue: editingEvent.venue || editingEvent.location || '',
        date: editingEvent.date ? eventDateTime.toISOString().split('T')[0] : '',
        time: editingEvent.time || (editingEvent.date ? eventDateTime.toTimeString().split(' ')[0].substring(0, 5) : ''),
        status: editingEvent.status || 'Pendiente',
        description: editingEvent.description || '',
        guests: editingEvent.guests ?? '',
        eventType: editingEvent.eventType || '',
        customFields: editingEvent.customFields || {}
      })
    } else {
      setFormData({
        name: '',
        venue: '',
        date: '',
        time: '',
        status: 'Pendiente',
        description: '',
        guests: '',
        eventType: '',
        customFields: {}
      })
      setPaymentPlan(emptyPaymentPlan())
    }
  }, [editingEvent])

  useEffect(() => {
    if (!open) setCurrentStep(0)
  }, [open])

  const steps = editingEvent
    ? ['Event details', 'Additional information']
    : ['Event details', 'Additional information', 'Payment plan']

  const validateStep = step => {
    if (step === 0 && (!selectedCustomer || !formData.name || !isRealVenueName(formData.venue) || !formData.date || !formData.eventType)) {
      alert('Please complete all required event details')
      return false
    }
    if (step === 1) {
      const missingDynamicField = dynamicFields.some(field => field.required && (!formData.customFields?.[field._id] || formData.customFields[field._id].length === 0))
      if (missingDynamicField) {
        alert('Please complete all required custom fields')
        return false
      }
    }
    return true
  }

  const goToNextStep = () => {
    if (validateStep(currentStep)) setCurrentStep(step => Math.min(step + 1, steps.length - 1))
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Math.max(0, parseInt(value, 10) || 0)) : value
    }))
    if (name === 'date' && !paymentPlan.payments?.[0]?.date) {
      setPaymentPlan(previous => ({ ...previous, payments: previous.payments.map((row, index) => index === 0 ? { ...row, date: value } : row) }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validate required fields
    const missingDynamicField = dynamicFields.some(field => field.required && (!formData.customFields?.[field._id] || formData.customFields[field._id].length === 0))
    if (!selectedCustomer || !formData.name || !isRealVenueName(formData.venue) || !formData.date || !formData.eventType || missingDynamicField) {
      alert('Please fill in all required fields')
      return
    }

    if (!editingEvent) {
      const planError = validatePaymentPlan(paymentPlan)
      if (planError) {
        alert(planError)
        return
      }
    }
    
    // Pass the formData directly instead of the event
    onSubmit(formData, editingEvent ? null : paymentPlan)
    
    if (!editingEvent) {
      setFormData({
        name: '',
        venue: '',
        date: '',
        time: '',
        status: 'Pendiente',
        description: '',
        guests: '',
        eventType: '',
        customFields: {}
      })
    }
  }

  return (
    <DrawerWrapper
      open={open}
      onClose={onClose}
      title={editingEvent ? 'Edit Event' : 'Create New Event'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-start gap-2" aria-label="Event creation progress">
          {steps.map((step, index) => (
            <React.Fragment key={step}>
              <button
                type="button"
                onClick={() => index < currentStep && setCurrentStep(index)}
                className={`flex min-w-0 flex-1 flex-col items-center gap-1 text-center ${index <= currentStep ? 'text-purple-700' : 'text-gray-400'}`}
                aria-current={index === currentStep ? 'step' : undefined}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${index < currentStep ? 'bg-purple-600 text-white' : index === currentStep ? 'border-2 border-purple-600 bg-purple-50' : 'border border-gray-300 bg-white'}`}>
                  {index < currentStep ? '✓' : index + 1}
                </span>
                <span className="text-[11px] font-medium leading-tight">{step}</span>
              </button>
              {index < steps.length - 1 && <div className={`mt-3 h-px flex-1 ${index < currentStep ? 'bg-purple-500' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Basic Information Section */}
        {currentStep === 0 && <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Basic Information
          </h3>
          
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Event Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter event name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Customer *
            </label>
            <select
              name="customer"
              required
              value={selectedCustomer || ''}
              onChange={(e) => {
                if (e.target.value === '__add_customer__') {
                  setShowCustomerModal(true)
                  return
                }
                setSelectedCustomer(e.target.value)
              }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select a customer</option>
              <option value="__add_customer__">+ Add New Customer</option>
              {customersList.map(customer => (
                <option key={customer._id} value={customer._id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Event Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Event Time
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Number of Guests
              </label>
              <input
                type="number"
                name="guests"
                value={formData.guests}
                onChange={handleInputChange}
                min="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Venue *
              </label>
              <select
                name="venue"
                value={isRealVenueName(formData.venue) ? formData.venue : ''}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select a venue</option>
                {isRealVenueName(formData.venue) && !sanitizeVenueNames(settings?.venueNames).includes(formData.venue) && <option value={formData.venue}>{formData.venue}</option>}
                {sanitizeVenueNames(settings?.venueNames).map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Event Type *
              </label>
              <select
                name="eventType"
                value={formData.eventType}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select event type</option>
                {eventTypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>}

        {currentStep === 1 && <>
        {dynamicFields.length > 0 && <div className="space-y-4"><h3 className="border-b border-gray-200 pb-2 text-sm font-medium text-gray-900">Custom Information</h3><DynamicFieldsEditor fields={dynamicFields} module="event" values={formData.customFields} onChange={customFields => setFormData(previous => ({ ...previous, customFields }))} /></div>}

        {/* Description Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Description
          </h3>
          
          <div>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className="w-full border border-gray-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Enter event description, special requirements, notes..."
            />
          </div>
        </div>
        </>}

        {!editingEvent && currentStep === 2 && <PaymentPlanEditor value={paymentPlan} onChange={setPaymentPlan} />}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          {currentStep === 0 ? (
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 py-2.5 rounded-lg font-medium transition-colors text-sm">Cancel</button>
          ) : (
            <button type="button" onClick={() => setCurrentStep(step => step - 1)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 py-2.5 rounded-lg font-medium transition-colors text-sm">Back</button>
          )}
          {currentStep < steps.length - 1 ? (
            <button type="button" onClick={goToNextStep} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm">Continue</button>
          ) : (
            <button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm">
              {editingEvent ? 'Update Event' : 'Create Event'}
            </button>
          )}
        </div>
      </form>
      <AddCustomerModal
        open={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCreated={onCustomerCreated}
      />
    </DrawerWrapper>
  )
}

export default EventDrawer
